export type PropSlot = 'head' | 'hand' | 'back' | 'accessory' | 'face' | 'hair';

export interface PropEffect {
  type: 'speed' | 'shield' | 'magnet' | 'vision' | 'teleport' | 'freeze' | 'bounce' | 'phase' | 'luck' | 'score';
  value: number;
  duration?: number;
  description: string;
}

export interface CharacterProp {
  id: string;
  name: string;
  description: string;
  slot: PropSlot;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  price: {
    coins?: number;
    gems?: number;
  };
  effects: PropEffect[];
  model: {
    color: string;
    secondaryColor?: string;
    icon?: string;
    glowColor?: string;
  };
}

export const PROPS_CATALOG: CharacterProp[] = [
  {
    id: 'wizard-hat',
    name: 'Wizard Hat',
    description: 'A mystical pointed hat that grants vision of hidden paths',
    slot: 'head',
    rarity: 'rare',
    price: { coins: 500 },
    effects: [
      { type: 'vision', value: 2, description: 'Reveals hidden tiles within 2 spaces' }
    ],
    model: {
      color: '#7B3FF2',
      secondaryColor: '#FFD700',
      glowColor: '#9D5CFF'
    }
  },
  {
    id: 'speed-sneakers',
    name: 'Lightning Sneakers',
    description: 'Rocket-powered shoes for blazing fast movement',
    slot: 'accessory',
    rarity: 'common',
    price: { coins: 200 },
    effects: [
      { type: 'speed', value: 1.5, duration: 10, description: 'Move 50% faster for 10 seconds' }
    ],
    model: {
      color: '#FF6B35',
      secondaryColor: '#FFEB3B',
      glowColor: '#FFA726'
    }
  },
  {
    id: 'shield-generator',
    name: 'Energy Shield',
    description: 'Portable force field generator that blocks one hit',
    slot: 'back',
    rarity: 'rare',
    price: { coins: 750 },
    effects: [
      { type: 'shield', value: 1, description: 'Blocks one collision with obstacles' }
    ],
    model: {
      color: '#00BCD4',
      secondaryColor: '#B2EBF2',
      glowColor: '#00E5FF'
    }
  },
  {
    id: 'magic-wand',
    name: 'Teleport Wand',
    description: 'Ancient wand that can teleport you short distances',
    slot: 'hand',
    rarity: 'epic',
    price: { gems: 50 },
    effects: [
      { type: 'teleport', value: 3, description: 'Teleport up to 3 tiles in any direction' }
    ],
    model: {
      color: '#E91E63',
      secondaryColor: '#FCE4EC',
      glowColor: '#FF4081'
    }
  },
  {
    id: 'lucky-clover',
    name: 'Lucky Clover',
    description: 'Four-leaf clover that increases coin collection',
    slot: 'accessory',
    rarity: 'common',
    price: { coins: 300 },
    effects: [
      { type: 'luck', value: 2, description: 'Double coin collection rate' }
    ],
    model: {
      color: '#4CAF50',
      secondaryColor: '#8BC34A',
      glowColor: '#69F0AE'
    }
  },
  {
    id: 'ice-crown',
    name: 'Frost Crown',
    description: 'Icy crown that freezes nearby obstacles',
    slot: 'head',
    rarity: 'epic',
    price: { gems: 75 },
    effects: [
      { type: 'freeze', value: 3, duration: 5, description: 'Freeze obstacles within 3 tiles for 5 seconds' }
    ],
    model: {
      color: '#81D4FA',
      secondaryColor: '#E1F5FE',
      glowColor: '#B3E5FC'
    }
  },
  {
    id: 'jetpack',
    name: 'Rocket Jetpack',
    description: 'High-tech jetpack for jumping over obstacles',
    slot: 'back',
    rarity: 'legendary',
    price: { gems: 150 },
    effects: [
      { type: 'bounce', value: 2, description: 'Jump over 2 tiles at a time' }
    ],
    model: {
      color: '#FF5722',
      secondaryColor: '#FFC107',
      glowColor: '#FF6E40'
    }
  },
  {
    id: 'ghost-pendant',
    name: 'Phase Pendant',
    description: 'Ethereal pendant that lets you phase through walls',
    slot: 'accessory',
    rarity: 'legendary',
    price: { gems: 200 },
    effects: [
      { type: 'phase', value: 1, duration: 3, description: 'Phase through walls for 3 seconds' }
    ],
    model: {
      color: '#9C27B0',
      secondaryColor: '#E1BEE7',
      glowColor: '#BA68C8'
    }
  },
  {
    id: 'magnet-glove',
    name: 'Magnetic Glove',
    description: 'Powerful electromagnet that attracts nearby coins',
    slot: 'hand',
    rarity: 'common',
    price: { coins: 400 },
    effects: [
      { type: 'magnet', value: 2, description: 'Attract coins within 2 tiles' }
    ],
    model: {
      color: '#607D8B',
      secondaryColor: '#CFD8DC',
      glowColor: '#90A4AE'
    }
  },
  {
    id: 'golden-trophy',
    name: 'Victory Trophy',
    description: 'Prestigious trophy that multiplies your score',
    slot: 'hand',
    rarity: 'epic',
    price: { gems: 100 },
    effects: [
      { type: 'score', value: 2, description: 'Double all points earned' }
    ],
    model: {
      color: '#FFD700',
      secondaryColor: '#FFF59D',
      glowColor: '#FFEB3B'
    }
  },
  {
    id: 'viking-helmet',
    name: 'Viking Helmet',
    description: 'Sturdy helmet with horns that breaks through ice',
    slot: 'head',
    rarity: 'common',
    price: { coins: 350 },
    effects: [
      { type: 'shield', value: 1, description: 'Break through ice obstacles without stopping' }
    ],
    model: {
      color: '#795548',
      secondaryColor: '#BCAAA4',
      glowColor: '#8D6E63'
    }
  },
  {
    id: 'dragon-wings',
    name: 'Dragon Wings',
    description: 'Majestic wings that let you glide over lava',
    slot: 'back',
    rarity: 'legendary',
    price: { gems: 250 },
    effects: [
      { type: 'phase', value: 1, description: 'Immune to lava damage' },
      { type: 'speed', value: 1.2, description: '20% movement speed bonus' }
    ],
    model: {
      color: '#D32F2F',
      secondaryColor: '#FFCDD2',
      glowColor: '#FF5252'
    }
  },
  {
    id: 'cool-glasses',
    name: 'Cool Glasses',
    description: 'Stylish sunglasses that improve your vision',
    slot: 'face',
    rarity: 'common',
    price: { coins: 150 },
    effects: [
      { type: 'vision', value: 1, description: 'See hidden tiles 1 space away' }
    ],
    model: {
      color: '#212121',
      secondaryColor: '#424242',
      glowColor: '#616161'
    }
  },
  {
    id: 'hipster-glasses',
    name: 'Hipster Glasses',
    description: 'Thick-rimmed glasses for the intellectual gamer',
    slot: 'face',
    rarity: 'common',
    price: { coins: 200 },
    effects: [
      { type: 'score', value: 1.1, description: '10% score bonus' }
    ],
    model: {
      color: '#8B4513',
      secondaryColor: '#A0522D',
      glowColor: '#CD853F'
    }
  },
  {
    id: 'afro-hair',
    name: 'Groovy Afro',
    description: 'A funky afro that boosts your luck',
    slot: 'hair',
    rarity: 'rare',
    price: { coins: 600 },
    effects: [
      { type: 'luck', value: 1.5, description: '50% better coin drops' }
    ],
    model: {
      color: '#3E2723',
      secondaryColor: '#5D4037',
      glowColor: '#6D4C41'
    }
  },
  {
    id: 'spiky-hair',
    name: 'Spiky Hair',
    description: 'Edgy spikes that increase your speed',
    slot: 'hair',
    rarity: 'common',
    price: { coins: 250 },
    effects: [
      { type: 'speed', value: 1.1, description: '10% speed increase' }
    ],
    model: {
      color: '#FFC107',
      secondaryColor: '#FFD54F',
      glowColor: '#FFE082'
    }
  },
  {
    id: 'fancy-moustache',
    name: 'Gentleman\'s Moustache',
    description: 'A distinguished moustache that commands respect',
    slot: 'face',
    rarity: 'rare',
    price: { coins: 500 },
    effects: [
      { type: 'score', value: 1.3, description: '30% score multiplier' }
    ],
    model: {
      color: '#3E2723',
      secondaryColor: '#4E342E',
      glowColor: '#5D4037'
    }
  },
  {
    id: 'gamer-headphones',
    name: 'Gamer Headphones',
    description: 'RGB headphones that enhance your focus',
    slot: 'head',
    rarity: 'epic',
    price: { gems: 60 },
    effects: [
      { type: 'freeze', value: 1, duration: 2, description: 'Slow time by 50% for 2 seconds' }
    ],
    model: {
      color: '#1A1A1A',
      secondaryColor: '#FF0080',
      glowColor: '#00FFFF'
    }
  },
  {
    id: 'pirate-beard',
    name: 'Pirate Beard',
    description: 'A rugged beard that attracts treasure',
    slot: 'face',
    rarity: 'epic',
    price: { gems: 40 },
    effects: [
      { type: 'magnet', value: 3, description: 'Attract coins within 3 tiles' },
      { type: 'luck', value: 1.3, description: '30% more coin value' }
    ],
    model: {
      color: '#5D4037',
      secondaryColor: '#6D4C41',
      glowColor: '#795548'
    }
  }
];